import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SourcingOpenings as SourcingOpeningsData } from 'api-clients/monolith';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { getDashboardOpenings } from '@/api-clients/get-dashboard-openings';
import { Pagination } from '@/components/base/pagination';
import { Search } from '@/components/base/search';
import { DataTable } from '@/components/ui/data-table';
import { NoHiringGoalDialogProvider } from '@/contexts/no-hiring-goal-dialog-context';
import {
  TableProvider,
  TableQueryParamsConfig,
  useTableContext,
} from '@/contexts/table-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { Filters } from '../filters';
import { useDashboardColumns } from './columns';
import { NoOpenings } from './no-openings';
import { RowsSelect } from './rows-select';

// Scoped to this table so changing the dashboard's rows-per-page preference
// never leaks into another table (e.g. All Openings) sharing `TableProvider`.
const ROWS_PER_PAGE_STORAGE_KEY = 'source.dashboard.rowsPerPage';
// Keeps page/search/sort/filters in the URL so the browser's back button
// returns to the same view after navigating away (e.g. into an opening's
// detail page), and so the current view is shareable via URL. Declared once
// at module scope so the config object identity is stable across renders.
const QUERY_PARAMS: TableQueryParamsConfig = {
  page: 'page',
  search: 'q',
  sortBy: 'sortBy',
  sortDirection: 'sortDir',
  filters: {
    brandIds: 'brands',
    positionIds: 'positions',
    locationIds: 'locations',
  },
};

type DashboardTableProps = {
  showAggregateData: boolean;
  setShowAggregateData: (showAggregateData: boolean) => void;
};

function DashboardTableContent({
  showAggregateData,
  setShowAggregateData,
}: DashboardTableProps) {
  const intl = useIntl();
  const {
    pagination: { pageIndex, pageSize },
    rowCount,
    setRowCount,
    searchQuery,
    setSearchQuery,
    filters: { positionIds, locationIds, brandIds },
    sorting,
  } = useTableContext();
  const sort = sorting[0];

  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.openings,
      pageIndex,
      pageSize,
      searchQuery,
      positionIds,
      locationIds,
      showAggregateData,
      brandIds,
      sorting,
    ],
    queryFn: () =>
      getDashboardOpenings({
        selectedForSourcing: true,
        page: pageIndex + 1,
        perPage: pageSize,
        query: searchQuery,
        positionIds,
        locationIds,
        showAggregateData,
        brandIds,
        sortBy: sort?.id,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      }),
  });

  if (isError) {
    throw new Error('Error fetching dashboard openings');
  }

  // invalidateQueries: callback invoked by RowsSelect on mutation success (cross-component)
  const refetchOpenings = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.dashboard.openings,
    });
  }, [queryClient]);

  const tableRows = useMemo(() => data?.data ?? [], [data]);
  const defaultMetricsPeriod = useMemo((): number | null | undefined => {
    if (data === undefined) return undefined;
    return data.default_metrics_period ?? null;
  }, [data]);

  const columns = useDashboardColumns(defaultMetricsPeriod, tableRows);

  useEffect(() => {
    setRowCount(data?.pagination?.total_count ?? 0);
  }, [data, setRowCount]);

  const { table } = useDataTable<SourcingOpeningsData>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'external_id',
    pinnedLeftColumns: [
      'select',
      'menu',
      'title',
      'performance_status_metrics',
      'total_hires',
      'total_duration',
    ],
  });

  // Remount Search when filters are cleared so its local input state resets.
  const [searchResetKey, setSearchResetKey] = useState(0);

  return (
    <>
      <div className="flex justify-between gap-2">
        <Filters onCleared={() => setSearchResetKey(key => key + 1)} />
        <Search
          key={searchResetKey}
          initialValue={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder={intl.formatMessage({
            defaultMessage: 'Search by opening name...',
            description: 'Placeholder for the search input',
          })}
        />
      </div>
      <DataTable
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyState={<NoOpenings searchQuery={searchQuery} />}
        showAggregateData={showAggregateData}
        setShowAggregateData={setShowAggregateData}
      />
      {tableRows.length > 0 && <Pagination table={table} />}
      <RowsSelect table={table} refetchOpenings={refetchOpenings} />
    </>
  );
}

export function DashboardTable({
  showAggregateData,
  setShowAggregateData,
}: DashboardTableProps) {
  return (
    <NoHiringGoalDialogProvider>
      <TableProvider
        pageSizeStorageKey={ROWS_PER_PAGE_STORAGE_KEY}
        queryParams={QUERY_PARAMS}
      >
        <DashboardTableContent
          showAggregateData={showAggregateData}
          setShowAggregateData={setShowAggregateData}
        />
      </TableProvider>
    </NoHiringGoalDialogProvider>
  );
}
